import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { FieldGroupKey, ListingInput, ListingTemplateInput, PublishTargetKey } from '@/api/listings'
import type { FormatOptions } from '@/design/format'
import { useCurrentTenant } from '@/lib/access'

/** The tenant's currency and locale, for every money figure on these pages. */
export function useMoney(): FormatOptions {
  const tenant = useCurrentTenant()
  return { currency: tenant.data?.currency ?? 'GBP', locale: tenant.data?.locale ?? 'en-GB' }
}

const LISTINGS_KEY = 'listings'
const TEMPLATES_KEY = 'listing-templates'

export function useListings() {
  const client = useApiClient()
  return useQuery({
    queryKey: [LISTINGS_KEY, client.kind],
    queryFn: () => client.listings(),
  })
}

export function useListing(id: string | undefined) {
  const client = useApiClient()
  return useQuery({
    queryKey: [LISTINGS_KEY, client.kind, id],
    queryFn: () => client.listing(id as string),
    enabled: Boolean(id),
  })
}

export function useListingTemplates() {
  const client = useApiClient()
  return useQuery({
    queryKey: [TEMPLATES_KEY, client.kind],
    queryFn: () => client.listingTemplates(),
  })
}

/** Every listing mutation invalidates the list and that listing's own query, so both stay in step. */
function useListingMutation<Args extends unknown[], Result>(
  mutate: (...args: Args) => Promise<Result>,
  idOf: (...args: Args) => string | undefined,
) {
  const queryClient = useQueryClient()
  const client = useApiClient()
  return useMutation({
    mutationFn: (args: Args) => mutate(...args),
    onSuccess: (_data, args) => {
      void queryClient.invalidateQueries({ queryKey: [LISTINGS_KEY, client.kind] })
      const id = idOf(...args)
      if (id) void queryClient.invalidateQueries({ queryKey: [LISTINGS_KEY, client.kind, id] })
    },
  })
}

export function useCreateListing() {
  const client = useApiClient()
  return useListingMutation(
    (input: ListingInput) => client.createListing(input),
    () => undefined,
  )
}

export function useUpdateListing() {
  const client = useApiClient()
  return useListingMutation(
    (id: string, input: ListingInput) => client.updateListing(id, input),
    (id: string) => id,
  )
}

export function useGenerateListingContent() {
  const client = useApiClient()
  return useListingMutation(
    (id: string) => client.generateListingContent(id),
    (id: string) => id,
  )
}

export function useSetListingApproval() {
  const client = useApiClient()
  return useListingMutation(
    (id: string, group: FieldGroupKey, approved: boolean) => client.setListingApproval(id, group, approved),
    (id: string) => id,
  )
}

export function usePublishListing() {
  const client = useApiClient()
  return useListingMutation(
    (id: string, target: PublishTargetKey) => client.publishListing(id, target),
    (id: string) => id,
  )
}

/** Shares its cache with the global filter bar's own query — same key, same data. */
export function useFilterOptions() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['filter-options', client.kind],
    queryFn: () => client.filterOptions(),
  })
}

export function useSaveListingTemplate() {
  const queryClient = useQueryClient()
  const client = useApiClient()
  return useMutation({
    mutationFn: (input: ListingTemplateInput) => client.saveListingTemplate(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, client.kind] }),
  })
}
